import { Component } from "react";
import { useLocation } from "react-router-dom";

/**
 * Without this, one error while drawing a page unmounts the whole app and leaves a
 * white screen (the address still changes). Now the page shows what went wrong and
 * a Reload button, and moving to another page clears it.
 */
class Boundary extends Component {
  state = { error: null };

  componentDidUpdate(prev) {
    // moving to another page clears the message; the boundary itself stays mounted so it can
    // also catch errors thrown while the previous page is being closed
    if (prev.at !== this.props.at && this.state.error) this.setState({ error: null });
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Page error:", error, info?.componentStack);
    // An old copy of the app asking for files a new deploy replaced: reload once to pick up the new one.
    if (/Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(String(error?.message || error))) {
      try {
        if (!sessionStorage.getItem("mt_reloaded")) {
          sessionStorage.setItem("mt_reloaded", "1");
          window.location.reload();
        }
      } catch {
        /* no storage — the Reload button still works */
      }
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const where = typeof window !== "undefined" ? window.location.pathname : "";
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="panel max-w-[520px] w-full p-6">
          <div className="eyebrow mb-1">MetaTrack</div>
          <h1 className="font-display text-[22px] font-semibold mb-2">This page hit a problem</h1>
          <p className="text-[13.5px] text-muted2 mb-4">Nothing was lost. Reload to try again, or go back. If it keeps happening, send a screenshot of this box.</p>
          <pre className="text-[11.5px] font-mono bg-surface border border-line rounded-lg p-3 whitespace-pre-wrap break-words max-h-48 overflow-auto mb-4">
            {where}
            {"\n"}
            {String(error?.message || error)}
          </pre>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => window.location.reload()} className="h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-semibold">
              Reload
            </button>
            <button type="button" onClick={() => window.history.back()} className="h-9 px-4 rounded-lg border border-line bg-card text-[13px] font-semibold">
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }
}

/** Resets on every move to another page, so one broken page doesn't stick. */
export default function PageErrorBoundary({ children }) {
  const { pathname, search } = useLocation();
  return <Boundary at={pathname + search}>{children}</Boundary>;
}
